import { useEffect, useMemo, useState } from "react";
import { PageHero } from "@/components/PageHero";
import { VehicleCard } from "@/components/VehicleCard";
import { Reveal } from "@/components/Reveal";
import { IMG } from "@/lib/site";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, SlidersHorizontal } from "lucide-react";

const BODY_TYPES = ["All", "Coupe", "Sedan", "SUV", "Truck"];

const PRICE_RANGES = [
  { label: "Any Price", value: "any" },
  { label: "Under $50k", value: "0-50000" },
  { label: "$50k – $75k", value: "50000-75000" },
  { label: "$75k – $100k", value: "75000-100000" },
  { label: "$100k+", value: "100000-9999999" },
];

const MILEAGE_RANGES = [
  { label: "Any Mileage", value: "any" },
  { label: "Under 15k mi", value: "15000" },
  { label: "Under 30k mi", value: "30000" },
  { label: "Under 50k mi", value: "50000" },
];

export default function Inventory() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [bodyType, setBodyType] = useState("All");
  const [make, setMake] = useState("any");
  const [price, setPrice] = useState("any");
  const [mileage, setMileage] = useState("any");

  useEffect(() => {
    document.title = "Inventory | TRX Auto Sales & Service Inc — Used Cars Charlotte & Statesville NC";
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const makes = useMemo(() => [...new Set(vehicles.map((v) => v.make))].sort(), [vehicles]);

  const filtered = useMemo(() => {
    return vehicles.filter((v) => {
      const q = search.trim().toLowerCase();
      if (q && !`${v.year} ${v.make} ${v.model} ${v.trim}`.toLowerCase().includes(q)) return false;
      if (bodyType !== "All" && v.body_type !== bodyType) return false;
      if (make !== "any" && v.make !== make) return false;
      if (price !== "any") {
        const [min, max] = price.split("-").map(Number);
        if (v.price < min || v.price > max) return false;
      }
      if (mileage !== "any" && v.mileage > Number(mileage)) return false;
      return true;
    });
  }, [vehicles, search, bodyType, make, price, mileage]);

  const selectCls =
    "h-12 border border-white/15 bg-black/40 px-4 text-sm text-zinc-300 outline-none transition-colors focus:border-trx-blue";

  return (
    <>
      <PageHero
        eyebrow="TRX Inventory"
        lines={["Quality Vehicles.", "Carefully Selected."]}
        sub="Every vehicle offered by TRX Auto Sales & Service Inc is selected with quality, reliability, and customer satisfaction in mind."
        image={IMG.porscheDark}
      />

      <section className="py-16 md:py-24" data-testid="inventory-section">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <Reveal>
            <div className="mb-6 flex flex-wrap gap-2" data-testid="body-type-filters">
              {BODY_TYPES.map((t) => (
                <button
                  key={t}
                  onClick={() => setBodyType(t)}
                  data-testid={`filter-body-${t.toLowerCase()}`}
                  className={`border px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-all duration-300 ${
                    bodyType === t
                      ? "border-trx-blue bg-trx-blue text-white"
                      : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mb-12 flex flex-col gap-4 border border-white/10 bg-card p-5 md:flex-row md:items-center" data-testid="inventory-filters">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search make, model, trim…"
                  data-testid="inventory-search"
                  className="h-12 rounded-none border-white/15 bg-black/40 pl-11 text-sm text-white placeholder:text-zinc-600 focus-visible:ring-trx-blue"
                />
              </div>
              <select value={make} onChange={(e) => setMake(e.target.value)} data-testid="inventory-make-filter" className={selectCls}>
                <option value="any">All Makes</option>
                {makes.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <select value={price} onChange={(e) => setPrice(e.target.value)} data-testid="inventory-price-filter" className={selectCls}>
                {PRICE_RANGES.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              <select value={mileage} onChange={(e) => setMileage(e.target.value)} data-testid="inventory-mileage-filter" className={selectCls}>
                {MILEAGE_RANGES.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </Reveal>

          <div className="mb-8 flex items-center gap-3 text-zinc-500">
            <SlidersHorizontal className="h-4 w-4" />
            <p className="text-xs font-semibold uppercase tracking-[0.2em]" data-testid="inventory-count">
              {filtered.length} vehicle{filtered.length === 1 ? "" : "s"} available
            </p>
          </div>

          {loading ? (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[16/14] animate-pulse border border-white/10 bg-white/5" />
              ))}
            </div>
          ) : filtered.length ? (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="inventory-grid">
              {filtered.map((v, i) => (
                <VehicleCard key={v.id} vehicle={v} index={i} />
              ))}
            </div>
          ) : (
            <div className="border border-white/10 py-24 text-center" data-testid="inventory-empty">
              <p className="font-display text-2xl font-bold text-white">No vehicles match your filters.</p>
              <p className="mt-3 text-sm text-zinc-500">Adjust your search — or call us. New inventory arrives weekly.</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
